import { useState, RefObject, useLayoutEffect } from 'react';

// Cart reader chip (owner 23 Oct): a reader name too long for the header row
// is cut to whole characters plus "…", so the gap before " · <word>" is the
// same as in "Main · Ready". (CSS ellipsis leaves up to a character's width
// of empty space after the "…".) The chip box spans the free part of the
// row, so its width never depends on the text and the fit cannot loop.
// Display only; any failure keeps the full name (CSS ellipsis still applies).
export const NAME_SELECTOR = '[data-reader-name]';
const ELLIPSIS = '…';

let canvas: HTMLCanvasElement | undefined;
const textWidth = (font: string) => {
  canvas = canvas ?? document.createElement('canvas');
  const context = canvas.getContext?.('2d');
  if (!context) {
    return undefined;
  }
  context.font = font;
  return (text: string) => context.measureText(text).width;
};

// The longest "<start of name>…" that fits `room` px (the whole name if it
// fits).
export const fitName = (
  name: string,
  room: number,
  measure: (text: string) => number
) => {
  if (measure(name) <= room) {
    return name;
  }
  // A cut name keeps 1 px spare for sub-pixel rounding between the canvas
  // and the page, so the browser's own "…" never adds its gap back.
  const cutRoom = room - 1;
  let low = 0;
  let high = name.length - 1;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measure(name.slice(0, mid).trimEnd() + ELLIPSIS) <= cutRoom) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return name.slice(0, low).trimEnd() + ELLIPSIS;
};

export const useFitName = (
  boxRef: RefObject<HTMLElement | null>,
  name: string | undefined,
  word: string | undefined
) => {
  const [shown, setShown] = useState(name);

  useLayoutEffect(() => {
    const box = boxRef.current;
    setShown(name);
    if (!box || !name) {
      return;
    }
    let frame = 0;
    const fit = () => {
      try {
        const badge = box.firstElementChild as HTMLElement | null;
        const nameEl = box.querySelector<HTMLElement>(NAME_SELECTOR);
        const measure = nameEl && textWidth(getComputedStyle(nameEl).font);
        if (!badge || !nameEl || !measure) {
          return;
        }
        // Everything in the pill but the name: padding, " · word", dot.
        const rest =
          badge.getBoundingClientRect().width -
          nameEl.getBoundingClientRect().width;
        const room = box.clientWidth - rest;
        setShown(fitName(name, room, measure));
      } catch {
        setShown(name);
      }
    };
    fit();
    document.fonts?.ready?.then(fit).catch(() => undefined);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(box);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [boxRef, name, word]);

  return shown;
};
